import React from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'semantic-ui-react';

const parseStringArray = val => {
  if (val === undefined || val === null) return [];
  try {
    // If it's already an array, return as-is (assume elements are strings)
    if (Array.isArray(val)) return val;
    // If it's a string, try to parse JSON
    if (typeof val === 'string') {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    }
    return [];
  } catch (e) {
    // On parse error, return empty array to avoid crashing the UI
    return [];
  }
};

const toDropdownOptions = (arr, ruleId) => arr.map(v => ({
  key: String(v),
  text: String(v),
  value: v,
  'data-testid': `multiselect-option-${ruleId}-${String(v)}`,
}));

const MultiselectValue = ({ rule, onValueChange }) => {
  // The full option catalog lives in the stored value: the FIELDS_CONFIG adapters
  // derive `options` from every entry and `value` from the enabled ones only, and
  // the reverse adapter rewrites just the `enabled` flag. Deselecting therefore
  // cannot drop an option, so the component needs no state of its own.
  const optionValues = parseStringArray(rule.options);
  const selectedValues = parseStringArray(rule.value);

  const options = toDropdownOptions(optionValues, rule.id);

  const persist = next => onValueChange(rule.instanceId, JSON.stringify(next));

  // Remove a single value, e.g. from a chip click, keeping the rest selected.
  const removeValue = value => persist(selectedValues.filter(v => v !== value));

  return (
    <Dropdown
      multiple
      selection
      options={options}
      value={selectedValues}
      onChange={(e, data) => persist(data.value)}
      placeholder='Select options...'
      data-testid={`multiselect-${rule.id}`}
      icon={(
        <i
          className='dropdown icon'
          data-testid={`multiselect-toggle-${rule.id}`}
        />
      )}
      renderLabel={item => ({
        content: item.text,
        style: { cursor: 'pointer' },
        'data-testid': `multiselect-chip-${rule.id}-${String(item.value)}`,
        // The delete icon is a small hit target — a click anywhere on the
        // chip removes it, overriding Semantic's default (marks it "active").
        onClick: e => {
          e.stopPropagation();
          removeValue(item.value);
        },
        removeIcon: (
          <i
            className='delete icon'
            data-testid={`multiselect-chip-delete-${rule.id}-${String(item.value)}`}
          />
        ),
      })}
    />
  );
};

MultiselectValue.propTypes = {
  rule: PropTypes.object.isRequired,
  onValueChange: PropTypes.func.isRequired,
};

export default MultiselectValue;
